import React, { useEffect, useState } from 'react';
import PageApp from '../../../Page';
import {
  HomeOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  UpOutlined,
  DownOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {
  Table,
  Empty,
  Divider,
  Button,
  message,
  Space,
  Tag,
  Form,
  Select,
  InputNumber,
  Card,
  Checkbox,
  Spin,
  Collapse,
} from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { getAll } from '../../../../apis/OdooApi';
import ConstanciaRetencionesPDFApi from '../../../../apis/ConstanciaRetencionesPDFApi';

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Nómina</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <FileTextOutlined />
        <span>Reportes</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <FileTextOutlined />
        <span>Constancias de Retenciones ISR</span>
      </>
    ),
  },
];

const ConstanciaRetencionesPDFPage = () => {
  const [form] = Form.useForm();
  const [empleados, setEmpleados] = useState([]);
  const [empleadosLoading, setEmpleadosLoading] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedConceptos, setSelectedConceptos] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);

  useEffect(() => {
    loadEmpleados();
  }, []);

  const loadEmpleados = async () => {
    try {
      setEmpleadosLoading(true);
      const response = await getAll();
      const rawEmpleados = Array.isArray(response?.payload)
        ? response.payload
        : Array.isArray(response)
          ? response
          : [];

      const activos = rawEmpleados.filter((emp) => {
        if (emp?.activo === undefined || emp?.activo === null) {
          return true;
        }
        return emp.activo === true || emp.activo === 1 || emp.activo === '1' || emp.activo === 'true' || emp.activo === 'Activo';
      });

      setEmpleados(
        activos.map((emp) => ({
          id: emp.id,
          name: emp.name || emp.nomina_name || `${emp.nomina_first_last_name || ''} ${emp.nomina_second_last_name || ''}`.trim(),
          employee_number: emp.nomina_employee_number || emp.employee_number || emp.numero_empleado || '-',
        }))
      );
    } catch (error) {
      message.error('Error al cargar empleados');
      console.error(error);
    } finally {
      setEmpleadosLoading(false);
    }
  };

  const handleObtenerPreview = async () => {
    try {
      const empleado_id = form.getFieldValue('odoo_empleado_id');
      const anio = form.getFieldValue('anio');

      if (!empleado_id || !anio) {
        message.warning('Por favor completa los campos de empleado y año');
        return;
      }

      setPreviewLoading(true);
      const response = await ConstanciaRetencionesPDFApi.obtenerPreview(empleado_id, anio);

      if (response && Array.isArray(response.conceptos)) {
        const conceptosOrdenados = response.conceptos
          .map((concepto, idx) => ({
            ...concepto,
            seleccionado: concepto.seleccionado !== false,
            orden: concepto.orden ?? idx,
          }))
          .sort((a, b) => a.orden - b.orden);

        setPreviewData(response);
        setSelectedConceptos(conceptosOrdenados);
        setShowPreview(true);
      } else {
        message.error('No se pudieron cargar los conceptos');
      }
    } catch (error) {
      message.error('Error al obtener preview: ' + (error.message || error));
      console.error(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const reindexConceptos = (items) =>
    items.map((item, idx) => ({
      ...item,
      orden: idx,
    }));

  const handleToggleConcepto = (index) => {
    const updated = [...selectedConceptos];
    updated[index].seleccionado = !updated[index].seleccionado;
    setSelectedConceptos(updated);
  };

  const moverConcepto = (index, direction) => {
    const updated = [...selectedConceptos];

    if (direction === 'arriba' && index > 0) {
      [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    }

    if (direction === 'abajo' && index < updated.length - 1) {
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    }

    setSelectedConceptos(reindexConceptos(updated));
  };

  const eliminarConcepto = (index) => {
    const updated = selectedConceptos.filter((_, i) => i !== index);
    setSelectedConceptos(reindexConceptos(updated));
  };

  const crearColumnasTabla = () => [
    {
      title: 'Sel',
      width: 50,
      render: (_, record, index) => (
        <Checkbox checked={record.seleccionado} onChange={() => handleToggleConcepto(index)} />
      ),
    },
    {
      title: 'Código',
      dataIndex: 'clave_concepto',
      width: 110,
      render: (value) => value || '-',
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto',
      render: (value) => value || '-',
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      width: 130,
      render: (monto) => `$${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Exento',
      dataIndex: 'exento',
      width: 130,
      render: (exento) => `$${(exento || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Descuento',
      dataIndex: 'descuento',
      width: 130,
      render: (descuento) => `$${(descuento || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Acciones',
      width: 140,
      render: (_, record, index) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<UpOutlined />}
            disabled={index === 0}
            onClick={() => moverConcepto(index, 'arriba')}
          />
          <Button
            type="text"
            size="small"
            icon={<DownOutlined />}
            disabled={index === selectedConceptos.length - 1}
            onClick={() => moverConcepto(index, 'abajo')}
          />
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => eliminarConcepto(index)}
          />
        </Space>
      ),
    },
  ];

  const handleGenerarPDFPersonalizado = async () => {
    try {
      setGeneratingPDF(true);
      const empleado_id = form.getFieldValue('odoo_empleado_id');
      const anio = form.getFieldValue('anio');

      if (!empleado_id || !anio) {
        message.warning('Por favor completa los campos');
        return;
      }

      const conceptosSeleccionados = selectedConceptos
        .filter((c) => c.seleccionado && c.concepto_id !== null && c.concepto_id !== undefined)
        .sort((a, b) => a.orden - b.orden)
        .map((c, idx) => ({
          concepto_id: c.concepto_id,
          clave_concepto: c.clave_concepto,
          seleccionado: true,
          orden: idx,
        }));

      if (conceptosSeleccionados.length === 0) {
        message.warning('Debes seleccionar al menos un concepto');
        return;
      }

      const config = {
        odoo_empleado_id: empleado_id,
        anio: anio,
        conceptos: conceptosSeleccionados,
      };

      await ConstanciaRetencionesPDFApi.generarPDFPersonalizado(config);
      message.success('PDF generado exitosamente');
      setShowPreview(false);
    } catch (error) {
      message.error('Error al generar PDF: ' + (error.message || error));
      console.error(error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleGenerarPDFDirecto = async () => {
    try {
      setGeneratingPDF(true);
      const empleado_id = form.getFieldValue('odoo_empleado_id');
      const anio = form.getFieldValue('anio');

      if (!empleado_id || !anio) {
        message.warning('Por favor completa los campos de empleado y año');
        return;
      }

      await ConstanciaRetencionesPDFApi.generarYDescargarPDF(empleado_id, anio);
      message.success('PDF descargado exitosamente');
    } catch (error) {
      message.error('Error al generar PDF: ' + (error.message || error));
      console.error(error);
    } finally {
      setGeneratingPDF(false);
    }
  };

  const conceptosSeleccionadosCount = selectedConceptos.filter((c) => c.seleccionado).length;

  return (
    <PageApp routes={routes} title="Constancias de Retenciones ISR">
      <Card style={{ marginBottom: 20 }}>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          initialValues={{
            anio: currentYear,
          }}
        >
          <Form.Item
            label="Empleado"
            name="odoo_empleado_id"
            rules={[{ required: true, message: 'Por favor selecciona un empleado' }]}
          >
            <Select
              placeholder="Selecciona un empleado..."
              loading={empleadosLoading}
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            >
              {empleados.map((emp) => (
                <Select.Option key={emp.id} value={emp.id} label={`${emp.name} (${emp.employee_number})`}>
                  {emp.name} ({emp.employee_number})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Año Fiscal"
            name="anio"
            rules={[
              { required: true, message: 'Por favor ingresa el año fiscal' },
              { type: 'number', min: 2000, max: currentYear, message: 'Año inválido' },
            ]}
          >
            <InputNumber min={2000} max={currentYear} style={{ width: '100%' }} />
          </Form.Item>

          <Space style={{ marginBottom: 20 }}>
            <Button type="primary" icon={<EyeOutlined />} loading={previewLoading} onClick={handleObtenerPreview}>
              Ver Conceptos
            </Button>
            <Button type="default" icon={<DownloadOutlined />} loading={generatingPDF} onClick={handleGenerarPDFDirecto}>
              Descargar PDF Directo
            </Button>
          </Space>
        </Form>
      </Card>

      {showPreview && previewData && (
        <Spin spinning={previewLoading}>
          <Card style={{ marginBottom: 24 }} title="Configuración de Conceptos">
            <Collapse
              style={{ marginBottom: 20 }}
              items={[
                {
                  key: '1',
                  label: 'Información del Empleado',
                  children: (
                    <div>
                      <p><strong>Nombre:</strong> {previewData.empleado_info?.name}</p>
                      <p><strong>RFC:</strong> {previewData.empleado_info?.rfc || '-'}</p>
                      <p><strong>CURP:</strong> {previewData.empleado_info?.curp || '-'}</p>
                      <p><strong>Número de Empleado:</strong> {previewData.empleado_info?.employee_number || '-'}</p>
                    </div>
                  ),
                },
                {
                  key: '2',
                  label: 'Información de la Empresa',
                  children: (
                    <div>
                      <p><strong>Empresa:</strong> {previewData.empresa_info?.name}</p>
                      <p><strong>RFC:</strong> {previewData.empresa_info?.rfc}</p>
                      <p><strong>Domicilio Fiscal:</strong> {previewData.empresa_info?.domicilio_fiscal}</p>
                    </div>
                  ),
                },
              ]}
            />

            <p style={{ color: '#666', marginBottom: 16 }}>
              Selecciona los conceptos a incluir, reordénalos con las flechas y elimina los que no necesites.
            </p>

            <Table
              columns={crearColumnasTabla()}
              dataSource={selectedConceptos}
              pagination={false}
              size="small"
              rowKey={(record, index) => record.concepto_id ?? `concepto-${index}`}
              locale={{ emptyText: <Empty description="Sin conceptos disponibles" /> }}
            />

            <Divider />

            <div
              style={{
                padding: 12,
                backgroundColor: '#f0f2f5',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <p>
                <strong>Conceptos seleccionados:</strong> {conceptosSeleccionadosCount} de {selectedConceptos.length}
              </p>
              <p>
                <strong>Orden visible:</strong> {selectedConceptos.filter((c) => c.seleccionado).length}
              </p>
            </div>

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowPreview(false)}>Cancelar</Button>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={generatingPDF}
                onClick={handleGenerarPDFPersonalizado}
              >
                Generar PDF Personalizado
              </Button>
            </Space>
          </Card>
        </Spin>
      )}
    </PageApp>
  );
};

export default ConstanciaRetencionesPDFPage;
